/**
 * **数据就绪度的会话级缓存**（2026-10-05 第十七批）。
 *
 * 为什么用一个 store 而不是每页各发一次 `useApiResource`：
 *  · 「数据齐到哪天」要出现在概览 / 广告管理 / 货号看板 / 广告指标表**多个页面**上，
 *    而它是**全局事实**（与所选店铺/区间无关）——每页各拉一次既浪费又是两份状态；
 *  · 端点本身已经刻意做轻（`/v1/data-readiness` 只回三个平台的真实数据日 + 预期产出日），
 *    但一次会话仍只该拉一次；页面切换直接命中缓存。
 *
 * 纪律：
 *  · 失败**不吞**（`state = "error"` + `error`），页面显示可重试的小字；
 *  · 在途请求去重（`inflight`），多个页面同时挂载只发一次；
 *  · `reload()` 作废在途请求（`generation`）后重发，不会出现旧响应覆盖新响应。
 */

import { create } from "zustand";
import type { DataReadinessData } from "../lib/apiTypes.js";
import { apiFetchDataReadiness } from "../lib/apiClient.js";
import type { ResourceState } from "../hooks/useApiResource.js";

export interface ReadinessStoreState {
  readonly data: DataReadinessData | null;
  readonly state: ResourceState;
  readonly error: unknown;
  /** 没有数据且没有在途请求时才发请求（页面挂载时调用）。 */
  readonly load: () => void;
  /** 强制重取（失败重试 / 同步后刷新）。 */
  readonly reload: () => void;
}

let inflight: Promise<void> | null = null;
let generation = 0;

export const useReadinessStore = create<ReadinessStoreState>((set, get) => {
  const run = (): void => {
    if (inflight !== null) return;
    const mine = (generation += 1);
    set({ state: "loading", error: null });
    inflight = apiFetchDataReadiness()
      .then((data) => {
        if (mine !== generation) return;
        set({ data, state: "ready", error: null });
      })
      .catch((error: unknown) => {
        if (mine !== generation) return;
        // 失败时保留旧 `data`（若有）：页面可以继续显示上一次的就绪度，但 state 是 error。
        set({ error, state: "error" });
      })
      .finally(() => {
        if (mine === generation) inflight = null;
      });
  };

  return {
    data: null,
    state: "loading",
    error: null,
    load: () => {
      if (get().data !== null || inflight !== null) return;
      run();
    },
    reload: () => {
      generation += 1; // 作废在途请求的结果
      inflight = null;
      run();
    },
  };
});
